import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Head } from 'vite-react-ssg';
import { Card, CardContent } from "@/components/ui/card";
import NavigationButtons from "@/components/NavigationButtons";
import RelatedLinks from "@/components/RelatedLinks";
import { DEEPSEEK_PRICING } from "@/lib/deepseek-pricing";

// Surcharge status is read from the same config as /pricing so this post
// can never claim surge pricing is live while `applies` is false.
const PEAK = DEEPSEEK_PRICING.peakSurcharge;
const SURCHARGE_ANSWER = PEAK.applies
  ? `Yes. DeepSeek applies a peak-hour surcharge during ${PEAK.windowUTC} UTC (${PEAK.windowUTC8} Beijing time).`
  : "No. DeepSeek has announced a peak-hour surcharge tied to Beijing time (UTC+8), but as of 25 July 2026 it is not active: no percentage and no start date have been published, and the official rate card still shows a single flat tier per model.";

const DeepseekV4GASurgePricing = () => {
  const navigate = useNavigate();
  const canonical = "https://deepseek.ai/blog/deepseek-v4-ga-surge-pricing-migration";

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline:
      "DeepSeek V4 Migration: Legacy API Aliases Retired July 24 — and Where Surge Pricing Actually Stands",
    datePublished: "2026-07-21T08:00:00+00:00",
    dateModified: "2026-07-26T08:00:00+00:00",
    author: { "@type": "Organization", name: "The Independent DeepSeek Guide", url: "https://deepseek.ai" },
    publisher: {
      "@type": "Organization",
      name: "The Independent DeepSeek Guide",
      logo: { "@type": "ImageObject", url: "https://deepseek.ai/og-image.png" },
    },
    description:
      "Legacy aliases deepseek-chat and deepseek-reasoner were retired on July 24, 2026. Migrate to deepseek-v4-flash or deepseek-v4-pro. DeepSeek's announced peak-hour surcharge (UTC+8) is not active yet.",
    mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "When do the legacy DeepSeek API aliases stop working?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Backward compatibility for the deepseek-chat and deepseek-reasoner aliases ends on July 24, 2026. After that date, calls to those model names return HTTP errors and must be migrated to deepseek-v4-flash or deepseek-v4-pro.",
        },
      },
      {
        "@type": "Question",
        name: "What are the new DeepSeek V4 model IDs?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Use deepseek-v4-flash for high-speed, cost-efficient tasks and deepseek-v4-pro for complex reasoning and heavy instruction-following. Both are served at https://api.deepseek.com in OpenAI-compatible format.",
        },
      },
      {
        "@type": "Question",
        name: "Is DeepSeek's peak-hour surcharge active?",
        acceptedAnswer: {
          "@type": "Answer",
          text: SURCHARGE_ANSWER,
        },
      },
      {
        "@type": "Question",
        name: "Does DeepSeek charge different rates by time of day?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Not today. DeepSeek's official rate card lists one flat per-million-token price per model. A peak-hour surcharge tied to Beijing time (UTC+8) has been announced, but no percentage or start date has been published and the API is still billed at the flat rate.",
        },
      },
    ],
  };

  return (
    <>
      <Head>
        <title>DeepSeek V4 GA: Legacy Aliases Retire July 24 + Surge Pricing</title>
        <meta
          name="description"
          content="DeepSeek V4 goes GA on July 24, 2026. Migrate from deepseek-chat/deepseek-reasoner to deepseek-v4-flash/pro and prepare for peak/off-peak surge pricing (UTC+8)."
        />
        <link rel="canonical" href={canonical} />

        <meta
          property="og:title"
          content="DeepSeek V4 Goes GA: July 24 Migration Deadline & AI's First Surge Pricing"
        />
        <meta
          property="og:description"
          content="Legacy DeepSeek API aliases retire July 24. Peak/off-peak pricing (UTC+8) rolls out. Full developer migration checklist inside."
        />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonical} />
        <meta property="og:image" content="https://deepseek.ai/og-image.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="DeepSeek V4 GA: July 24 Migration + Surge Pricing" />
        <meta
          name="twitter:description"
          content="What developers need to do before DeepSeek retires legacy API aliases on July 24, 2026."
        />
        <meta name="twitter:image" content="https://deepseek.ai/og-image.png" />

        <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Head>

      <div className="min-h-screen bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-12">
          <Button
            variant="ghost"
            onClick={() => navigate("/blog")}
            className="mb-8 hover:bg-gray-100"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Blog
          </Button>

          <NavigationButtons />

          <article className="transition-opacity duration-300 opacity-100">
            <div className="mb-8">
              <div className="text-gray-500 mb-2">July 21, 2026 · updated July 26, 2026 • Deep Seek Fan Hub</div>
              <h1 className="text-4xl font-bold text-gray-900 mb-6">
                DeepSeek V4 Migration: Legacy API Aliases Retired July 24 — and Where Surge Pricing Actually Stands
              </h1>

              <div className="bg-blue-50 p-6 rounded-lg border border-blue-100 mb-8">
                <p className="text-gray-800">
                  <strong>Update, 26 July 2026:</strong> the legacy alias retirement went ahead on <strong>July 24, 2026</strong> — <code className="px-1 bg-gray-100 rounded">deepseek-chat</code> and <code className="px-1 bg-gray-100 rounded">deepseek-reasoner</code> no longer resolve. The <strong>peak-hour surcharge</strong> did <strong>not</strong> go live with it. It has been announced, but no percentage and no start date are published and the official rate card still lists a single flat tier per model. See our <a href="/pricing" className="text-blue-600 hover:underline">verified pricing page</a> for the live status.
                </p>
              </div>
            </div>


            <div className="prose prose-lg max-w-none mb-12">
              <section>
                <h2 className="text-2xl font-bold mt-10 mb-6">1. Hard Deadline: Legacy Aliases Retire July 24</h2>
                <p>
                  If your codebases, SDK configs or environment variables still route requests to <code className="px-1 bg-gray-100 rounded">deepseek-chat</code> or <code className="px-1 bg-gray-100 rounded">deepseek-reasoner</code>, migrate now. DeepSeek has been silently forwarding those aliases to V4 Flash under the hood, but backward compatibility ends on <strong>July 24, 2026</strong>. After that, calls to legacy model names return HTTP errors.
                </p>
                <p>Update your endpoints directly to the V4 model IDs:</p>
                <ul className="list-disc pl-6 space-y-3">
                  <li>
                    <strong>deepseek-v4-flash</strong> — high-speed, cost-efficient default for most chat, extraction and classification workloads.
                  </li>
                  <li>
                    <strong>deepseek-v4-pro</strong> — complex reasoning, heavy instruction-following and long-context work up to 1M tokens.
                  </li>
                </ul>
                <p>
                  Base URL and OpenAI-compatible request shape are unchanged (<code className="px-1 bg-gray-100 rounded">https://api.deepseek.com</code>). See our <a href="/deepseek-api" className="text-blue-600 hover:underline">DeepSeek API guide</a> and <a href="/pricing" className="text-blue-600 hover:underline">pricing page</a> for full rate cards.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold mt-10 mb-6">2. Time-of-Day Surge Pricing: Announced, Not Active</h2>
                <p>
                  The change that got the most attention was a <strong>peak-hour surcharge</strong> tied to Beijing business hours (UTC+8). It is worth being precise about its status, because a lot of coverage reported it as live:
                </p>
                <ul className="list-disc pl-6 space-y-3">
                  <li>
                    <strong>Announced</strong> — DeepSeek has said a peak-hour surcharge is coming, with the peak window framed around Beijing daytime ({PEAK.windowUTC8} UTC+8).
                  </li>
                  <li>
                    <strong>Not in effect</strong> — no surcharge percentage and no start date have been published, and billing today runs on one flat per-million-token rate per model.
                  </li>
                </ul>
                <Card className="my-6 border-blue-200 bg-blue-50/40">
                  <CardContent className="pt-6">
                    <p className="m-0">
                      <strong>Developer takeaway:</strong> you do not need to reschedule batch jobs yet. What is worth doing now is instrumenting requests with a timestamp so that, if and when the surcharge lands, you can attribute spend to peak versus off-peak windows without a retrofit. We re-check the official rate card weekly and this page reads its status from the same config as <a href="/pricing" className="text-blue-600 hover:underline">/pricing</a>.
                    </p>
                  </CardContent>
                </Card>
                <p>
                  Historically DeepSeek ran off-peak <em>discounts</em>; that programme has ended. The announced surcharge inverts the mechanism — same load-balancing goal, opposite framing — but until it ships there is no time-of-day component in a DeepSeek invoice.
                </p>
              </section>



              <section>
                <h2 className="text-2xl font-bold mt-10 mb-6">3. V4 Performance: Holding the Price-to-Performance Crown</h2>
                <p>
                  DeepSeek built its reputation by delivering frontier-adjacent quality at a fraction of Western API prices. V4 doubles down: V4-Pro competes with GPT-5.5 and Claude Opus 4.8 on reasoning benchmarks, and V4-Flash remains one of the cheapest capable models on the market. Even as open-weight rivals like Kimi K3 push parameter counts higher, DeepSeek V4 keeps its edge on the <strong>speed × capability × cost</strong> triangle.
                </p>
                <p>
                  For a deeper look at how DeepSeek is squeezing more out of every GPU, see our write-ups on <a href="/blog/inside-deepseek-dspark-lossless-inference" className="text-blue-600 hover:underline">DSpark speculative decoding</a> and <a href="/blog/deepseek-own-ai-chip-inference-silicon" className="text-blue-600 hover:underline">DeepSeek's custom inference chip</a>.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold mt-10 mb-6">Developer Migration Checklist</h2>
                <ul className="list-disc pl-6 space-y-3">
                  <li>
                    <strong>Update endpoints:</strong> Audit code and swap <code className="px-1 bg-gray-100 rounded">deepseek-chat</code> / <code className="px-1 bg-gray-100 rounded">deepseek-reasoner</code> for <code className="px-1 bg-gray-100 rounded">deepseek-v4-flash</code> or <code className="px-1 bg-gray-100 rounded">deepseek-v4-pro</code>.
                  </li>
                  <li>
                    <strong>Do not reschedule yet:</strong> there is no time-of-day rate today, so moving cron jobs buys nothing until the announced surcharge actually ships.
                  </li>
                  <li>
                    <strong>Verify fallback logic:</strong> handle latency spikes and transient errors gracefully — retries, timeouts, degraded modes.
                  </li>
                  <li>
                    <strong>Instrument cost:</strong> log timestamp + model + token count per request so you can attribute spend to peak vs off-peak windows the day a surcharge lands.
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold mt-10 mb-6">The Bottom Line</h2>
                <p>
                  The July 24 alias retirement is the part that actually happened: every production integration now has to make an explicit Flash-versus-Pro choice. The surge pricing story is still a plan, not an invoice line. Treat it as something to be instrumented for, not something to reorganise your scheduling around — and check <a href="/pricing" className="text-blue-600 hover:underline">our pricing page</a>, which tracks the official rate card weekly, before assuming otherwise.
                </p>
              </section>

              <section className="mt-12 bg-gray-50 p-6 rounded-lg">
                <h2 className="text-2xl font-bold mb-6">FAQ: DeepSeek V4 Migration &amp; Surge Pricing</h2>
                <div className="space-y-6">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">When did the legacy DeepSeek API aliases stop working?</h3>
                    <p>Backward compatibility for <code className="px-1 bg-gray-100 rounded">deepseek-chat</code> and <code className="px-1 bg-gray-100 rounded">deepseek-reasoner</code> ended on July 24, 2026. Requests to those model names now return HTTP errors.</p>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">What are the new DeepSeek V4 model IDs?</h3>
                    <p>Use <strong>deepseek-v4-flash</strong> for high-speed, cost-efficient tasks and <strong>deepseek-v4-pro</strong> for complex reasoning. Both remain OpenAI-compatible on <code className="px-1 bg-gray-100 rounded">https://api.deepseek.com</code>.</p>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Is DeepSeek's peak-hour surcharge active?</h3>
                    <p>{SURCHARGE_ANSWER}</p>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Does DeepSeek charge different rates by time of day?</h3>
                    <p>Not today. The official rate card lists one flat per-million-token price per model. The announced Beijing-hours surcharge has no published percentage or start date.</p>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Should I default to V4-Flash or V4-Pro?</h3>
                    <p>Start with V4-Flash for latency-sensitive and high-volume workloads and reserve V4-Pro for reasoning-heavy or long-context tasks where quality clearly justifies the cost.</p>
                  </div>
                </div>
              </section>
            </div>

            <div className="border-t border-gray-200 pt-8 mt-8">
              <p className="text-gray-600 italic">
                Migrating a production workload before July 24? Let us know how surge pricing affects your batch scheduling in the comments below.
              </p>
            </div>
          </article>

          <div className="mt-12">
            <RelatedLinks />
          </div>
        </div>
      </div>
    </>
  );
};

export default DeepseekV4GASurgePricing;
